// Pestaña «Vegetación y obra»: mapa de hotspots de pérdida de vegetación 2019→2026
// + lista filtrable + ficha con antes/después y la explicación del análisis.

import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import type { FeatureCollection, Geometry } from 'geojson';
import { ArrowLeft, Building2, Crosshair, ExternalLink, Leaf, MapPin, Sprout, AlertTriangle } from 'lucide-react';

import { Card, EmptyState, ErrorNote, Loading } from '@/modules/orion-shell/ui';
import {
  CATEGORIAS, COBERTURA_LABEL, ORDEN_CATEGORIAS, entero, hotspotImg, loadHotspots, num,
  type Categoria, type Collection, type Feature, type HotspotProps, type Resumen,
} from './data';
import { CARTO_KEY, reducedMotion, useTerritoryMap } from './useTerritoryMap';
import { Stat } from './Stat';
import { BaseSwitch } from './BaseSwitch';

type Filtro = Categoria | 'todas';

export function HotspotsView({ resumen }: { resumen: Resumen }) {
  const [data, setData] = useState<Collection<HotspotProps> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('probable_obra_o_desmonte');
  const [municipio, setMunicipio] = useState<string>('');
  const [sel, setSel] = useState<number | null>(null);
  const { map, setNode, base, setBase } = useTerritoryMap('2026');
  const layerRef = useRef<L.GeoJSON | null>(null);
  const pinsRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    loadHotspots().then(setData).catch((e) => setError(e instanceof Error ? e.message : 'No se pudieron cargar los hotspots.'));
  }, []);

  const municipios = useMemo(() => {
    const s = new Set<string>();
    data?.features.forEach((f) => s.add(f.properties.municipio));
    return [...s].sort((a, b) => a.localeCompare(b, 'es'));
  }, [data]);

  const visibles = useMemo(() => {
    if (!data) return [];
    return data.features
      .filter((f) => (filtro === 'todas' || f.properties.categoria === filtro) && (!municipio || f.properties.municipio === municipio))
      .sort((a, b) => a.properties.rank - b.properties.rank);
  }, [data, filtro, municipio]);

  const seleccionado = useMemo(
    () => (sel == null ? null : data?.features.find((f) => f.properties.rank === sel) ?? null),
    [data, sel],
  );

  // Capa de polígonos + pines numerados (top 20 de lo visible).
  useEffect(() => {
    if (!map) return;
    layerRef.current?.remove();
    pinsRef.current?.remove();
    const gj = L.geoJSON(
      { type: 'FeatureCollection', features: visibles } as FeatureCollection,
      {
        style: (f) => {
          const p = f?.properties as HotspotProps;
          const activo = p.rank === sel;
          return { color: CATEGORIAS[p.categoria].color, weight: activo ? 3.5 : 1.5, fillOpacity: activo ? 0.08 : 0.32, opacity: 0.95 };
        },
        onEachFeature: (f, layer) => {
          const p = f.properties as HotspotProps;
          layer.bindTooltip(`#${p.rank} · ${num(p.area_ha)} ha · ${CATEGORIAS[p.categoria].corto}`, { direction: 'top', sticky: true });
          layer.on('click', () => setSel(p.rank));
        },
      },
    ).addTo(map);
    layerRef.current = gj;
    const pins = L.layerGroup();
    visibles.slice(0, 20).forEach((f) => {
      const p = f.properties;
      const icon = L.divIcon({
        className: '',
        html: `<span class="gt-pin" data-active="${p.rank === sel}" style="background:${CATEGORIAS[p.categoria].color}">${p.rank}</span>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });
      L.marker([p.centroide_lat, p.centroide_lon], { icon, keyboard: false, title: `Hotspot #${p.rank}` })
        .on('click', () => setSel(p.rank))
        .addTo(pins);
    });
    pins.addTo(map);
    pinsRef.current = pins;
  }, [map, visibles, sel]);

  // Encadrar: al cambiar filtros, todo lo visible; al elegir uno, ese polígono.
  useEffect(() => {
    if (!map || !layerRef.current || sel != null) return;
    const b = layerRef.current.getBounds();
    if (b.isValid()) map.fitBounds(b, { padding: [24, 24], maxZoom: 14, animate: !reducedMotion() });
  }, [map, filtro, municipio, data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map || !seleccionado) return;
    const b = L.geoJSON(seleccionado.geometry as Geometry).getBounds();
    if (b.isValid()) map.flyToBounds(b, { padding: [60, 60], maxZoom: 15, animate: !reducedMotion(), duration: 0.6 });
  }, [map, seleccionado]);

  const v = resumen.vegetacion;
  const obra = v.por_categoria.probable_obra_o_desmonte;
  const munTop = Object.entries(v.por_municipio_obra).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={<Leaf size={18} />} tone="violet" label="Vegetación perdida" value={`${num(v.area_total_ha)} ha`}
          sub={`${entero(v.n_hotspots)} polígonos ≥ ${v.umbrales.area_min_ha} ha`} />
        <Stat icon={<Building2 size={18} />} tone="magenta" label="Con señal de obra o desmonte" value={`${num(obra.ha)} ha`}
          sub={`${entero(obra.n)} hotspots`} />
        <Stat icon={<MapPin size={18} />} tone="indigo" label="Municipio con más obra" value={munTop ? munTop[0].replace(' de Graciano Sánchez', '') : '—'}
          sub={munTop ? `${num(munTop[1])} ha` : undefined} />
        <Stat icon={<Sprout size={18} />} tone="blue" label="Periodo comparado" value={`${v.fechas_antes[0].slice(0, 4)} → ${v.fechas_despues[0].slice(0, 4)}`}
          sub="Temporada seca (mar–may)" />
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
        <Card className="overflow-hidden" bodyClassName="!p-0">
          <div className="gt-map relative">
            <div ref={setNode} className="h-[380px] w-full sm:h-[520px] lg:h-[600px]" role="region" aria-label="Mapa de hotspots de pérdida de vegetación" />
            <BaseSwitch value={base} onChange={setBase} />
            {!CARTO_KEY && base === 'mapa' && (
              <p className="gt-nobase rounded-md bg-card/90 px-2 py-0.5 text-[10px] text-muted-foreground">Mapa base no configurado · se muestran solo los datos</p>
            )}
            <div className="pointer-events-none absolute bottom-6 left-3 z-[500] flex flex-col gap-1 rounded-xl border border-border bg-card/90 px-3 py-2 text-[11px] text-foreground shadow-sm backdrop-blur">
              {ORDEN_CATEGORIAS.map((c) => (
                <span key={c} className="flex items-center gap-2">
                  <span aria-hidden className="h-2.5 w-2.5 rounded-sm" style={{ background: CATEGORIAS[c].color }} />
                  {CATEGORIAS[c].corto}
                </span>
              ))}
            </div>
          </div>
        </Card>

        <Card bodyClassName="!pt-4 flex flex-col gap-3 min-h-0">
          {error && <ErrorNote>{error}</ErrorNote>}
          {!data && !error && <Loading label="Cargando hotspots…" />}
          {data && !seleccionado && (
            <>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por categoría">
                {(['probable_obra_o_desmonte', 'revisar', 'probable_rotacion_agricola', 'todas'] as Filtro[]).map((k) => {
                  const activo = filtro === k;
                  const n = k === 'todas' ? data.features.length : v.por_categoria[k].n;
                  return (
                    <button key={k} type="button" aria-pressed={activo} onClick={() => setFiltro(k)}
                      className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-medium transition-colors ${
                        activo ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
                      {k !== 'todas' && <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: CATEGORIAS[k].color }} />}
                      {k === 'todas' ? 'Todas' : CATEGORIAS[k].corto}
                      <span className="tabular-nums opacity-70">{n}</span>
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Municipio
                <select value={municipio} onChange={(e) => setMunicipio(e.target.value)}
                  className="h-9 min-w-0 flex-1 rounded-xl border border-input bg-card px-2 text-sm text-foreground">
                  <option value="">Toda la zona metropolitana</option>
                  {municipios.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <p className="text-xs text-muted-foreground">
                {filtro === 'todas' ? 'Todas las categorías' : CATEGORIAS[filtro].ayuda}
              </p>
              <div className="-mx-2 flex max-h-[420px] flex-col gap-1 overflow-y-auto px-2 lg:max-h-[440px]">
                {visibles.length === 0 && (
                  <EmptyState icon={<Crosshair size={18} />} title="Sin hotspots con este filtro">
                    Prueba otra categoría o toda la zona metropolitana.
                  </EmptyState>
                )}
                {visibles.map((f) => <HotspotRow key={f.properties.rank} f={f} onOpen={() => setSel(f.properties.rank)} />)}
              </div>
            </>
          )}
          {seleccionado && <HotspotDetail f={seleccionado} resumen={resumen} onBack={() => setSel(null)} />}
        </Card>
      </div>
    </div>
  );
}

function HotspotRow({ f, onOpen }: { f: Feature<HotspotProps>; onOpen: () => void }) {
  const p = f.properties;
  const c = CATEGORIAS[p.categoria];
  return (
    <button type="button" onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-xl border border-transparent px-2 py-2 text-left transition-colors hover:border-border hover:bg-muted/50">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[12px] font-semibold tabular-nums text-white" style={{ background: c.color }} aria-hidden>
        {p.rank}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {num(p.area_ha)} ha · {p.municipio}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          NDVI {num(p.ndvi_antes, 2)} → {num(p.ndvi_despues, 2)} · {c.corto}
        </span>
      </span>
      <span className="sr-only">Abrir ficha del hotspot {p.rank}</span>
    </button>
  );
}

function HotspotDetail({ f, resumen, onBack }: { f: Feature<HotspotProps>; resumen: Resumen; onBack: () => void }) {
  const p = f.properties;
  const c = CATEGORIAS[p.categoria];
  const v = resumen.vegetacion;
  const cobertura = Object.entries(p.worldcover2021).filter(([, x]) => x >= 0.02).sort((a, b) => b[1] - a[1]);
  const coords = `${p.centroide_lat.toFixed(5)}, ${p.centroide_lon.toFixed(5)}`;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onBack} className="inline-flex h-9 items-center gap-1.5 rounded-xl px-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
          <ArrowLeft size={16} aria-hidden /> Lista
        </button>
        <span className={`ml-auto rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${c.chip}`}>{c.label}</span>
      </div>
      <div>
        <h3 className="text-lg font-semibold tracking-tight text-foreground">Hotspot #{p.rank} · {num(p.area_ha)} ha</h3>
        <p className="text-xs text-muted-foreground">{p.municipio} · {coords}</p>
      </div>

      {p.img ? (
        <div className="grid grid-cols-2 gap-2">
          {(['antes', 'despues'] as const).map((k) => (
            <figure key={k} className="overflow-hidden rounded-xl border border-border bg-muted">
              <img src={hotspotImg(p.rank, k)} alt={`Sentinel-2 ${k === 'antes' ? 'antes' : 'después'} del hotspot ${p.rank}`}
                className="aspect-square w-full object-cover" loading="lazy" />
              <figcaption className="px-2 py-1 text-[11px] text-muted-foreground">
                {k === 'antes' ? `Antes · ${v.fechas_antes[0].slice(0, 4)}` : `Después · ${v.fechas_despues[0].slice(0, 4)}`}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          Recorte antes/después disponible para los 30 primeros hotspots.
        </p>
      )}

      <div className="rounded-xl bg-muted/50 p-3 text-sm leading-relaxed text-foreground">
        <span className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Sprout size={13} aria-hidden /> Por qué está aquí
        </span>
        {p.explicacion}
      </div>

      <dl className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl border border-border p-2">
          <dt className="text-[11px] text-muted-foreground">NDVI antes</dt>
          <dd className="text-base font-semibold tabular-nums text-foreground">{num(p.ndvi_antes, 2)}</dd>
        </div>
        <div className="rounded-xl border border-border p-2">
          <dt className="text-[11px] text-muted-foreground">NDVI después</dt>
          <dd className="text-base font-semibold tabular-nums text-foreground">{num(p.ndvi_despues, 2)}</dd>
        </div>
        <div className="rounded-xl border border-border p-2">
          <dt className="text-[11px] text-muted-foreground">Δ NDBI</dt>
          <dd className="text-base font-semibold tabular-nums text-foreground">{p.dndbi > 0 ? '+' : ''}{num(p.dndbi, 2)}</dd>
        </div>
      </dl>

      {cobertura.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Qué había en 2021 (ESA WorldCover)</p>
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
            {cobertura.map(([k, x], i) => (
              <span key={k} style={{ width: `${x * 100}%`, background: `hsl(var(--primary) / ${Math.max(0.25, 1 - i * 0.22)})` }} />
            ))}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {cobertura.map(([k, x]) => `${COBERTURA_LABEL[k] ?? k} ${Math.round(x * 100)} %`).join(' · ')}
          </p>
        </div>
      )}

      <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
        <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden />
        A 10 m el satélite no distingue suelo desnudo de construcción, así que hay que confirmarlo con imagen de alta resolución o en campo. Sin validación de campo.
      </p>
      <a href={`https://www.openstreetmap.org/?mlat=${p.centroide_lat}&mlon=${p.centroide_lon}#map=17/${p.centroide_lat}/${p.centroide_lon}`}
        target="_blank" rel="noreferrer"
        className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted">
        Ver ubicación en OpenStreetMap <ExternalLink size={14} aria-hidden />
      </a>
    </div>
  );
}
